// BrainMonitor.js — internal (isDev) X-ray of the ambient pipeline.
// Live chunk timer with VAD state, the water line (provisional window the
// brain is still re-reading), and a feed of every round trip: what Whisper
// heard, what got committed to which visit, what actions came back, how the
// window moved, and how long the trip took. 🧠 chip in the panel header.
import React, { useEffect, useRef, useState } from 'react';
import {
  ambientInspector,
  MIN_CHUNK_MS,
  HARD_CUT_MS,
  RMS_SPEECH,
} from 'agent/ambient';
import { useComprehendLive } from 'agent/useComprehendLive';
import { T } from './liveTheme';

const mono = {
  fontFamily: 'SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 10,
};

const Section = ({ title, right, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <p
        style={{
          margin: '0 0 4px',
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '.6px',
          color: T.textFaint,
          flex: 1,
        }}
      >
        {title}
      </p>
      {right}
    </div>
    {children}
  </div>
);

const LiveChunk = () => {
  // Poll the inspector's live snapshot — cheaper than eventing every 150ms.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 200);
    return () => clearInterval(t);
  }, []);

  const live = ambientInspector.live;
  if (!live) {
    return (
      <p style={{ margin: 0, fontSize: 10, color: T.textFaint }}>
        ambient is off — open the panel / grant mic
      </p>
    );
  }
  if (live.paused) {
    return (
      <p style={{ margin: 0, fontSize: 10, color: T.amber }}>
        paused ({live.paused === 'cap' ? '1-hour cap' : 'silence'}) — collapse
        and reopen the panel to resume
      </p>
    );
  }
  const now = Date.now();
  const age = now - live.startedAt;
  const sinceVoice = now - live.lastVoiceAt;
  const voiced = live.rms >= RMS_SPEECH;
  const agePct = Math.min(100, (age / HARD_CUT_MS) * 100);
  const armPct = (MIN_CHUNK_MS / HARD_CUT_MS) * 100;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ ...mono, color: T.textSoft }}>#{live.seq}</span>
        <span style={{ ...mono, color: T.textSoft }}>
          {(age / 1000).toFixed(1)}s
        </span>
        <span
          className={voiced ? 'cpl-dot' : undefined}
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: voiced ? T.greenBright : 'rgba(159,232,200,0.25)',
            display: 'inline-block',
          }}
        />
        <span style={{ ...mono, color: voiced ? T.greenBright : T.textFaint }}>
          {voiced ? 'VOICE' : `quiet ${(sinceVoice / 1000).toFixed(1)}s`}
        </span>
        <span style={{ ...mono, color: T.textFaint, marginLeft: 'auto' }}>
          rms {live.rms.toFixed(3)}
        </span>
      </div>
      {/* chunk age bar: VAD arms at the 10s tick, hard cut at the end */}
      <div
        style={{
          position: 'relative',
          height: 6,
          marginTop: 5,
          borderRadius: 3,
          background: 'rgba(159,232,200,0.10)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${agePct}%`,
            background:
              age >= MIN_CHUNK_MS ? T.amber : 'rgba(107,216,136,0.55)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${armPct}%`,
            top: 0,
            bottom: 0,
            width: 1,
            background: 'rgba(255,255,255,0.45)',
          }}
        />
      </div>
      {/* live RMS meter */}
      <div
        style={{
          height: 3,
          marginTop: 3,
          borderRadius: 2,
          background: 'rgba(159,232,200,0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.min(100, (live.rms / 0.08) * 100)}%`,
            background: voiced ? T.greenBright : T.textFaint,
          }}
        />
      </div>
    </>
  );
};

const ChunkEvent = ({ ev }) => {
  const [open, setOpen] = useState(false);
  // Never collapse these two into one label: "silence" is the FE energy gate
  // choosing not to spend a Whisper call, "corrupt chunk" is real speech we
  // captured and then had to throw away. They need opposite fixes.
  if (ev.type === 'skipped') {
    const why =
      ev.cause === 'corrupt-chunk'
        ? 'corrupt chunk — no container header'
        : `silence — ${ev.voicedMs || 0}ms voiced`;
    return (
      <p
        style={{
          ...mono,
          margin: '0 0 5px',
          color: ev.cause === 'corrupt-chunk' ? T.amber : T.textFaint,
        }}
      >
        #{ev.seq} · {ev.durationSec}s · skipped ({why})
      </p>
    );
  }
  // An explicit agent turn owns these words — dropped on purpose, not lost.
  if (ev.type === 'suppressed') {
    return (
      <p style={{ ...mono, margin: '0 0 5px', color: T.textFaint }}>
        #{ev.seq} · {ev.durationSec}s · {ev.reason} · held (agent turn)
      </p>
    );
  }
  if (ev.type === 'autostop') {
    return (
      <p style={{ ...mono, margin: '0 0 5px', color: T.amber }}>
        ⏸ auto-stopped —{' '}
        {ev.reason === 'cap' ? '1-hour session cap' : '20 silent chunks'} (demo
        safety limit)
      </p>
    );
  }
  const failed = Boolean(ev.error);
  const border = failed ? T.red : ev.brainError ? T.amber : T.greenBright;
  return (
    <div
      onClick={() => setOpen(!open)}
      style={{
        borderLeft: `2px solid ${border}`,
        background: T.surfaceCard,
        borderRadius: 6,
        padding: '5px 7px',
        marginBottom: 5,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ ...mono, color: T.textPrimary, fontWeight: 700 }}>
          #{ev.seq}
        </span>
        <span style={{ ...mono, color: T.textSecondary }}>
          {ev.durationSec}s·{ev.reason}
          {ev.forced ? '·⚡' : ''}
        </span>
        <span style={{ ...mono, color: T.textFaint }}>
          {(ev.latencyMs / 1000).toFixed(1)}s rt
        </span>
        <span style={{ ...mono, color: T.textFaint, marginLeft: 'auto' }}>
          {failed
            ? 'FAILED'
            : ev.brainError
              ? 'BRAIN ERR'
              : `${(ev.committed || []).length}✎ ${ev.gate?.act ? '⚙' : '·'}`}
        </span>
      </div>
      {failed && (
        <p style={{ ...mono, margin: '3px 0 0', color: T.redSoft }}>
          {ev.error}
        </p>
      )}
      {!failed && (
        <p
          style={{
            ...mono,
            margin: '3px 0 0',
            color: T.textSoft,
            ...(open
              ? {}
              : {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }),
          }}
        >
          {ev.newText ? (
            `“${ev.newText}”`
          ) : ev.noise ? (
            <span style={{ color: T.textFaint }}>
              🚫 filtered as noise: “{ev.noise}”
            </span>
          ) : (
            '(no speech transcribed)'
          )}
        </p>
      )}
      {(ev.committed || []).map((c, i) => (
        <p
          key={i}
          style={{
            ...mono,
            margin: '3px 0 0',
            color: T.greenBright,
            ...(open
              ? {}
              : {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
          }}
        >
          ✎ {c.conversationId}: {c.text}
        </p>
      ))}
      {ev.gate?.act && ev.gate?.instruction && (
        <p
          style={{
            ...mono,
            margin: '3px 0 0',
            color: T.tealSoft,
            ...(open
              ? {}
              : {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
          }}
        >
          ⚙ gate → agent: {ev.gate.instruction}
        </p>
      )}
      {!failed && (
        <p style={{ ...mono, margin: '3px 0 0', color: T.textFaint }}>
          window {ev.windowBefore}→{ev.windowAfter} ch
          {ev.lastSpeaker ? ` · with ${ev.lastSpeaker}` : ''}
          {ev.liveTargets === 0 ? ' · listen-only' : ''}
        </p>
      )}
    </div>
  );
};

const BrainMonitor = ({ onClose }) => {
  const { state, actions } = useComprehendLive();
  const [, force] = useState(0);
  const feedRef = useRef(null);
  useEffect(
    () => ambientInspector.emitter.on('update', () => force((n) => n + 1)),
    [],
  );

  const listening = state.ambientStatus === 'listening';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 7,
        background: T.surfaceDeep,
        display: 'flex',
        flexDirection: 'column',
        padding: '10px 12px',
        animation: 'cplRise .18s ease-out',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13 }}>🧠</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.6px',
            color: T.textPrimary,
          }}
        >
          AMBIENT BRAIN
        </span>
        <span
          className={listening ? 'cpl-dot' : undefined}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            display: 'inline-block',
            background: listening ? T.greenBright : T.textFaint,
          }}
        />
        <span
          onClick={actions.ambientCatchUp}
          role="button"
          title="Flush the accumulator now (forced)"
          style={{
            marginLeft: 'auto',
            color: T.amber,
            fontSize: 12,
            cursor: 'pointer',
            padding: 4,
          }}
        >
          <i className="fa fa-bolt" aria-hidden="true" />
        </span>
        <i
          className="fa fa-times"
          aria-hidden="true"
          onClick={onClose}
          style={{
            color: T.textSecondary,
            fontSize: 12,
            cursor: 'pointer',
            padding: 4,
          }}
        />
      </div>

      <Section title="LIVE CHUNK — vad arms at 10s, hard cut 20s">
        <LiveChunk />
      </Section>

      <Section
        title="WATER LINE — provisional, brain re-reads each chunk"
        right={
          <span style={{ ...mono, color: T.textFaint }}>
            {ambientInspector.window.length}/4000
            {ambientInspector.lastSpeaker
              ? ` · with ${ambientInspector.lastSpeaker}`
              : ''}
          </span>
        }
      >
        <div
          className="cpl-scroll"
          style={{
            maxHeight: 64,
            overflowY: 'auto',
            background: 'rgba(13,35,30,0.55)',
            border: `1px solid ${T.border}`,
            borderRadius: 6,
            padding: '5px 7px',
          }}
        >
          <p style={{ ...mono, margin: 0, color: T.textSecondary }}>
            {ambientInspector.window || (
              <span style={{ color: T.textFaint }}>
                empty — everything settled
              </span>
            )}
          </p>
        </div>
      </Section>

      <Section title={`ROUND TRIPS (${ambientInspector.events.length})`} />
      <div
        ref={feedRef}
        className="cpl-scroll"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: -6 }}
      >
        {ambientInspector.events.length === 0 && (
          <p style={{ ...mono, color: T.textFaint }}>
            nothing yet — speak, then watch the cut land here
          </p>
        )}
        {ambientInspector.events.map((ev) => (
          <ChunkEvent key={ev.id} ev={ev} />
        ))}
      </div>
    </div>
  );
};

export default BrainMonitor;
